import React, { useRef, useEffect } from 'react';

/**
 * Six-box OTP input that behaves like a single text field.
 * - Numeric only (letters silently rejected)
 * - Auto-advance to next box on input
 * - Backspace into prev box when current is empty
 * - Paste of a 6-digit string fills all boxes
 *
 * Props:
 *   value     — string (max 6 digits)
 *   onChange  — (newValue: string) => void
 *   autoFocus — focus first box on mount
 *   testidPrefix — base data-testid for individual boxes
 */
export default function OTPInput({ value = '', onChange, autoFocus = true, testidPrefix = 'otp' }) {
  const refs = useRef([]);

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  const digits = value.padEnd(6, ' ').slice(0, 6).split('');

  const update = (idx, char) => {
    if (char && !/^\d$/.test(char)) return;
    const arr = digits.map((d) => (d === ' ' ? '' : d));
    arr[idx] = char;
    const next = arr.join('').replace(/\s/g, '');
    onChange(next);
    if (char && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx].trim() && idx > 0) {
      e.preventDefault();
      const arr = digits.map((d) => (d === ' ' ? '' : d));
      arr[idx - 1] = '';
      onChange(arr.join('').replace(/\s/g, ''));
      refs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    onChange(txt);
    refs.current[Math.min(txt.length, 5)]?.focus();
  };

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid={`${testidPrefix}-input-group`}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digits[i] === ' ' ? '' : digits[i]}
          onChange={(e) => update(i, e.target.value.slice(-1))}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className="w-11 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-mono font-semibold border-2 border-slate-300 rounded-md focus:border-[hsl(210,45%,40%)] focus:outline-none focus:ring-2 focus:ring-[hsl(210,45%,40%)]/20 bg-white transition-colors"
          aria-label={`Digit ${i + 1} of 6`}
          data-testid={`${testidPrefix}-box-${i}`}
        />
      ))}
    </div>
  );
}
